import React, { useRef, useState } from "react";
import { ChevronRight, Trash2 } from "lucide-react";
import { DocumentText, Receipt21 } from "iconsax-reactjs";
import { Button } from "../../../../shared/ui";

export interface PaymentInfoData {
  note: string;
  invoiceFile: File | null;
  invoiceFileName: string;
  supportingDocFile: File | null;
  supportingDocFileName: string;
}

export interface PaymentInformationStepProps {
  initialData: PaymentInfoData;
  onBack: () => void;
  onNext: (data: PaymentInfoData) => void;
}

export const PaymentInformationStep: React.FC<PaymentInformationStepProps> = ({
  initialData,
  onBack,
  onNext,
}) => {
  const [note, setNote] = useState(initialData.note || "");
  const [invoiceFile, setInvoiceFile] = useState<File | null>(
    initialData.invoiceFile
  );
  const [invoiceFileName, setInvoiceFileName] = useState(
    initialData.invoiceFileName || ""
  );
  const [supportingDocFile, setSupportingDocFile] = useState<File | null>(
    initialData.supportingDocFile
  );
  const [supportingDocFileName, setSupportingDocFileName] = useState(
    initialData.supportingDocFileName || ""
  );

  const invoiceInputRef = useRef<HTMLInputElement>(null);
  const supportingDocInputRef = useRef<HTMLInputElement>(null);

  const handleInvoiceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setInvoiceFile(file);
      setInvoiceFileName(file.name);
    }
  };

  const handleSupportingDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSupportingDocFile(file);
      setSupportingDocFileName(file.name);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext({
      note,
      invoiceFile,
      invoiceFileName: invoiceFileName || (invoiceFile ? invoiceFile.name : ""),
      supportingDocFile,
      supportingDocFileName:
        supportingDocFileName || (supportingDocFile ? supportingDocFile.name : ""),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      {/* Title */}
      <h2 className="text-[20px] font-semibold text-neutral-900">
        Enter payment information
      </h2>

      {/* Add note */}
      <div className="flex flex-col gap-1.5">
        <p className="text-[13px] font-medium text-neutral-700">Add note</p>
        <textarea
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Write a note here"
          className="w-full resize-none rounded-[8px] border border-transparent bg-surface-input p-3.5 text-[14px] text-neutral-900 placeholder:text-neutral-400 focus:border-brand focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-ring transition-all"
        />
      </div>

      {/* Supporting documents */}
      <div className="flex flex-col gap-2.5">
        <p className="text-[14px] font-normal text-[#475467]">
          Supporting documents
        </p>

        {/* Hidden inputs */}
        <input
          ref={invoiceInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.png,.jpg,.jpeg"
          onChange={handleInvoiceChange}
        />
        <input
          ref={supportingDocInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.png,.jpg,.jpeg"
          onChange={handleSupportingDocChange}
        />

        {/* Supporting documents card */}
        <div className="flex flex-col gap-4 rounded-[8px] border border-neutral-100 bg-white p-4 shadow-xs">
          {/* Document Row 1: Invoice */}
          <div
            onClick={() => invoiceInputRef.current?.click()}
            className="flex cursor-pointer items-center justify-between rounded-lg py-1 transition-opacity hover:opacity-80"
          >
            <div className="flex items-center gap-3">
              <Receipt21 size={22} variant="Bold" color="#A173FF" />
              <span className="text-[14px] font-medium text-[#101828]">
                Invoice
              </span>
            </div>

            <div className="flex items-center gap-2">
              {invoiceFileName ? (
                <div className="flex items-center gap-2">
                  <span className="max-w-[160px] truncate text-[13px] font-medium text-[#A173FF]">
                    {invoiceFileName}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setInvoiceFile(null);
                      setInvoiceFileName("");
                    }}
                    className="rounded p-1 text-neutral-400 hover:text-danger-600"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ) : (
                  <div className="flex items-center gap-1.5 text-[14px] text-[#667085]">
                    <span>No invoice</span>
                  <ChevronRight size={18} className="text-[#667085]" />
                </div>
              )}
            </div>
          </div>

          {/* Document Row 2: Other document */}
          <div
            onClick={() => supportingDocInputRef.current?.click()}
            className="flex cursor-pointer items-center justify-between rounded-lg py-1 transition-opacity hover:opacity-80"
          >
            <div className="flex items-center gap-2">
              <DocumentText size={22} variant="Bold" color="#A173FF" />
              <span className="text-[14px] font-medium text-[#101828]">
                Other document
              </span>
              <span className="text-[13px] font-normal italic text-[#667085]">
                Optional
              </span>
            </div>

            <div className="flex items-center gap-2">
              {supportingDocFileName ? (
                <div className="flex items-center gap-2">
                  <span className="max-w-[160px] truncate text-[13px] font-medium text-[#A173FF]">
                    {supportingDocFileName}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSupportingDocFile(null);
                      setSupportingDocFileName("");
                    }}
                    className="rounded p-1 text-neutral-400 hover:text-danger-600"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ) : (
                  <div className="flex items-center gap-1.5 text-[14px] text-[#667085]">
                    <span>No document</span>
                  <ChevronRight size={18} className="text-[#667085]" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex items-center justify-end gap-3 border-t border-neutral-100 pt-4">
        <button
          type="button"
          onClick={onBack}
          className="h-10 rounded-[8px] px-4 py-2 text-[14px] font-medium text-neutral-700 transition-colors hover:text-neutral-900"
        >
          Back
        </button>
        <Button
          type="submit"
          className="px-6"
        >
          Confirm
        </Button>
      </div>
    </form>
  );
};
